/** Pure helpers behind the institution usage panel (STORY-100).
 *
 * Kept free of value imports so Node's own test runner can load them: `usageApi.ts` re-exports
 * them for the screens and adds the calls, which need the api client and its browser globals.
 */
import type { components } from "../api/analytics";

export type InstitutionUsage = components["schemas"]["InstitutionUsage"];
export type UsageMonth = components["schemas"]["UsageMonth"];
export type UsageTotals = components["schemas"]["UsageTotals"];
export type BandTotals = components["schemas"]["BandTotals"];
export type CourseUsageOut = components["schemas"]["CourseUsageOut"];
export type CourseUsageRow = components["schemas"]["CourseUsageRow"];
export type InstitutionRow = components["schemas"]["InstitutionRow"];

export type MonthRange = { from: string; to: string };

const pad = (n: number) => String(n).padStart(2, "0");

/** `YYYY-MM` of a date, in UTC: the service's months are UTC months. */
export function monthOf(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

/** Every month from `from` to `to` inclusive, across year boundaries; empty when reversed. */
export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  if (!y || !m) return out;
  while (`${y}-${pad(m)}` <= to) {
    out.push(`${y}-${pad(m)}`);
    if (m === 12) { y += 1; m = 1; } else m += 1;
    if (out.length > 120) break;
  }
  return out;
}

/** The last `span` months ending in the month of `now` (the service's own default is six). */
export function defaultRange(now: Date, span = 6): MonthRange {
  const to = monthOf(now);
  let y = Number(to.slice(0, 4));
  let m = Number(to.slice(5, 7)) - (span - 1);
  while (m < 1) { m += 12; y -= 1; }
  return { from: `${y}-${pad(m)}`, to };
}

/** The bar scale: never zero, so an all-zero series draws empty bars instead of NaN widths. */
export function seriesMax(values: number[]): number {
  let max = 0;
  for (const v of values) if (Number.isFinite(v) && v > max) max = v;
  return max > 0 ? max : 1;
}

/** "Sep 2026" / "сент. 2026 г." in the UI language; the raw `YYYY-MM` when it does not parse. */
export function monthLabel(month: string, lang: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang, { month: "short", year: "numeric", timeZone: "UTC" });
}

export const pct = (share: number | null | undefined): number | null =>
  share == null ? null : Math.round(share * 100);

/** The month table as CSV, in the service's own field names; a null is an empty cell. */
export function usageCsv(months: UsageMonth[]): string {
  const cols = ["month", "sessions", "courses_active", "students", "answers", "answers_per_student", "correct_share"] as const;
  const cell = (v: unknown) => (v == null ? "" : String(v));
  return [cols.join(","), ...months.map((m) => cols.map((c) => cell(m[c])).join(","))].join("\n") + "\n";
}
